import { getRedirectResult, GoogleAuthProvider, onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { getAuth } from 'firebase/auth';
import React, { useEffect, useState, useRef } from 'react';
import { useAppContext } from './AppContext';
import { useNavigate } from 'react-router';
import { FaSignInAlt } from "react-icons/fa";
import { MdOutlineFollowTheSigns, MdMailOutline } from "react-icons/md";
import { app } from '../utils/firebase';
import ActionButton from './ActionButton';
import SelectComponent from './SelectComponent';
import User from './User';
import './Login.scss';


export default function Login() {

    const {appData, setAppData} = useAppContext();
    const navigate = useNavigate();
    // Preserve install flag through login so the install banner appears after sign-in
    const installParam = new URLSearchParams(window.location.search).get('install') === '1' ? '?install=1' : '';
    const [showEmailLogin, setShowEmailLogin] = useState(false);
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [newEmail, setNewEmail] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [newDisplayName, setNewDisplayName] = useState<string>('');
    const [manualLoginError, setManualLoginError] = useState<string | null>(null);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotStatus, setForgotStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

    // Currency selection step for new users
    const [pendingLoginData, setPendingLoginData] = useState<{ apiData: any; firebaseUser: any } | null>(null);
    const [currencies, setCurrencies] = useState<{ code: string; name: string }[]>([]);
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [savingCurrency, setSavingCurrency] = useState(false);
    const loginInProgressRef = useRef(false);
    const appDataRef = useRef(appData);
    appDataRef.current = appData;

    useEffect(() => {
        const CACHE_KEY = 'currencies_cache';
        const CACHE_TS_KEY = 'currencies_cache_ts';
        const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
        const parseCached = (raw: string | null) => { try { return raw ? JSON.parse(raw) as { code: string; name: string }[] : null; } catch { return null; } };
        const fromApi = () => fetch('/api/currencies')
            .then(r => r.json())
            .then((data: Record<string, string>) => {
                const list = Object.entries(data).map(([code, name]) => ({ code, name }));
                localStorage.setItem(CACHE_KEY, JSON.stringify(list));
                localStorage.setItem(CACHE_TS_KEY, String(Date.now()));
                return list;
            });
        const cached = parseCached(localStorage.getItem(CACHE_KEY));
        const cachedAt = Number(localStorage.getItem(CACHE_TS_KEY) || 0);
        const stale = Date.now() - cachedAt > CACHE_TTL_MS;
        if (cached) { setCurrencies(cached); if (stale) fromApi().then(setCurrencies).catch(() => {}); }
        else { fromApi().then(setCurrencies).catch(() => {}); }
    }, []);

    const auth = getAuth(app);

    // Handle google submission
    const loginWithGoogle = (e: any) => {
        e.preventDefault();
        const provider = new GoogleAuthProvider();

        if (window.location.hostname === 'localhost') {
            signInWithPopup(auth, provider);
        } else {
            signInWithPopup(auth, provider);
        }
    };

        const handlePostLogin = React.useCallback(async (user: any) => {

                 if (loginInProgressRef.current) return;
                 loginInProgressRef.current = true;

                 // Establish server-side session cookie from the Firebase ID token
                 try {
                    const idToken = await user.getIdToken();
                    await fetch('/api/sessionLogin', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ idToken }),
                    });
                 } catch (e) {
                    console.error('Error establishing session:', e);
                 }

                 // Check if user exists in Azure Table, if not, insert
                    // Check or insert user using backend
                    await fetch('/api/login', {
                            method: 'POST',
                            headers: {
                                    'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                    firebaseUid: user?.uid,
                                    user: {
                                    user_id: user?.uid,
                                    user_email: user?.email || '',
                                    user_name: user?.displayName
                            }})
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        return response.json();
                    }).then(data => {
                        //console.log('User retrieved or inserted in Azure Table:', data.user_email);
                        const currency = data.currency || '';
                        const userName = data.user_name || user.displayName || '';
                        if (!currency) {
                            // New user — store credentials and show currency picker before navigating
                            localStorage.setItem('signed_in_id', data.user_id);
                            localStorage.setItem('signed_in', 'true');
                            localStorage.setItem('signed_in_email', data.user_email);
                            localStorage.setItem('user_name', userName);
                            localStorage.setItem('currency', '');
                            setAppData({ ...appDataRef.current, user: { ...appDataRef.current.user, userId: data.user_id, userEmail: data.user_email, userName, signedIn: true, currency: '' } });
                            setPendingLoginData({ apiData: data, firebaseUser: user });
                            return;
                        }
                        setAppData({ ...appDataRef.current, loading: true, user: { ...appDataRef.current.user, userId: data.user_id, userEmail: data.user_email, userName, signedIn: true, currency } });
                        localStorage.setItem('signed_in_id', data.user_id);
                        localStorage.setItem('signed_in', "true");
                        localStorage.setItem('signed_in_email', data.user_email);
                        localStorage.setItem('user_name', userName);
                        localStorage.setItem('currency', currency);
                        navigate("/" + installParam)
                    }).catch(error => {
                        //console.error('Error in login API call:', error);
                    }).finally(() => {
                        loginInProgressRef.current = false;
                    });
        }, [setAppData, navigate, installParam]);

    const handleFinalizeCurrency = async () => {
        if (!selectedCurrency || !pendingLoginData) return;
        setSavingCurrency(true);
        try {
            const { apiData } = pendingLoginData;
            await fetch('/api/updateUser', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: apiData.user_id, currency: selectedCurrency }),
            });
            localStorage.setItem('currency', selectedCurrency);
            setAppData({ ...appData, loading: true, user: { ...appData.user, currency: selectedCurrency } });
            // Fire-and-forget welcome provisioning
            fetch('/api/provision-welcome', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: apiData.user_id, currency: selectedCurrency }),
            }).catch(() => {});
            setPendingLoginData(null);
            navigate('/' + installParam);
            // Schedule a second fetch after provisioning completes (fire-and-forget so takes a few seconds)
            setTimeout(() => setAppData({ loading: true }), 6000);
        } catch (err) {
            console.error('Error saving currency:', err);
        } finally {
            setSavingCurrency(false);
        }
    };

    // Handle email/password submission
    const createEmailLogin = async (e: any) => {
    e.preventDefault();

        try {
            const { createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');
            const userCredential = await createUserWithEmailAndPassword(auth, newEmail, newPassword);
            await updateProfile(userCredential.user, { displayName: newDisplayName });
            //console.log('Account created:', userCredential.user.uid);
            handlePostLogin(userCredential.user);
            //navigate('/');
          } catch (error: any) {
            //console.error('Error creating account:', error.code, error.message);
          }

        return;
    };

    useEffect(() => {
        getRedirectResult(auth).then(result => {
            //console.log("Redirect result:", result);
            if (result?.user) {
            //console.log('Signed in:', result.user);
            handlePostLogin(result.user);
            }
        });
    }, [auth, handlePostLogin]);

    useEffect(() => {
        
        const unsubscribe = onAuthStateChanged(auth, user => {
            //console.log('User is signed in:', user);
            if (!appDataRef.current.user.signedIn && user) {
                handlePostLogin(user);
            } else if (user && !appDataRef.current.user.userName && user.displayName) {
                const name = user.displayName;
                localStorage.setItem('user_name', name);
                setAppData({ ...appDataRef.current, user: { ...appDataRef.current.user, userName: name } });
            }
        });
        return () => unsubscribe();
    
    }, [auth, handlePostLogin]);

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!forgotEmail) return;
        setForgotStatus('sending');
        try {
            await fetch('/api/forgotPassword', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail }),
            });
        } catch {}
        setForgotStatus('sent');
    };

    const handleManualSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        setManualLoginError(null);
    
        if (!email || !password) {
          setManualLoginError('Please enter both email and password.');
          return;
        }
    
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          //console.log('Successfully signed in manually:', userCredential.user.uid);
          handlePostLogin(userCredential.user);
          //navigate("/")
        } catch (error: any) {
          //console.error('Error during manual sign-in:', error.code, error.message);
          let errorMessage = 'An unknown error occurred during sign-in.';
          switch (error.code) {
            case 'auth/invalid-email':
              errorMessage = 'Invalid email address format.';
              break;
            case 'auth/user-not-found':
            case 'auth/wrong-password':
              errorMessage = 'Invalid email or password.';
              break;
            case 'auth/too-many-requests':
              errorMessage = 'Too many failed login attempts. Please try again later.';
              break;
            default:
              errorMessage = error.message;
          }
          setManualLoginError(errorMessage);
        }
        //navigate("/")
    };

    return (
        <div>
            {pendingLoginData && (
                <div className="currency-wizard-style3">
                    <svg className="currency-wizard-style3__icon" width="64" height="64" viewBox="0 0 48 48" aria-hidden="true">
                        <circle cx="24" cy="24" r="22" fill="none" stroke="#000" strokeWidth="3"/>
                        <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fontSize="22" fill="#000">$</text>
                    </svg>
                    <p className="currency-wizard-style3__label">
                        One last step
                    </p>
                    <h2 className="currency-wizard-style3__heading">
                        Select your home currency
                    </h2>
                    <p className="currency-wizard-style3__body">
                        sConscious will convert all your receipts to this currency. You can change it later in your profile.
                    </p>
                    <div className="currency-wizard-style3__card">
                        <SelectComponent
                            value={selectedCurrency}
                            onChange={setSelectedCurrency}
                            options={currencies.map(c => ({ value: c.code, label: `${c.code} — ${c.name}` }))}
                            height="2rem"
                        />
                    </div>
                    <div className="currency-wizard-style3__actions">
                        <button
                            className="currency-wizard-style3__cta"
                            disabled={!selectedCurrency || savingCurrency}
                            onClick={handleFinalizeCurrency}
                        >
                            {savingCurrency ? 'Setting up...' : 'Continue'}
                        </button>
                    </div>
                </div>
            )}
            {!pendingLoginData && (
            <div>

            {
                appData.user.signedIn ? 
                    
                    <>
                        <User />
                    </>
                :
                    <>
                    <p>Login</p>
                        <ActionButton
                            label="Google"
                            onClick={loginWithGoogle}
                            ariaLabel="Google"
                            icon={
                                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                                    <path fill="#FFC107" d="M43.611 20.083h-1.611v-.083H24v8h11.303c-1.651 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.152 7.957 3.043l5.657-5.657C34.332 6.053 29.457 4 24 4 12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20c0-1.341-.138-2.651-.389-3.917z"/>
                                    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.69 16.215 18.961 13 24 13c3.059 0 5.842 1.152 7.957 3.043l5.657-5.657C34.332 6.053 29.457 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                                    <path fill="#4CAF50" d="M24 44c5.367 0 10.237-2.047 13.928-5.385l-6.43-5.423C29.318 35.909 26.791 37 24 37c-5.199 0-9.615-3.317-11.283-7.946l-6.52 5.022C9.594 39.556 16.292 44 24 44z"/>
                                    <path fill="#1976D2" d="M43.611 20.083h-1.611v-.083H24v8h11.303c-.766 2.159-2.19 4.005-4.005 5.295l.003-.002 6.43 5.423C36.413 39.414 44 34 44 24c0-1.341-.138-2.651-.389-3.917z"/>
                                </svg>
                            }
                        />
                        <br/>
                        <br/>
                        <ActionButton
                            label="sConscious"
                            onClick={() => setShowEmailLogin(true)}
                            ariaLabel="sConscious"
                            icon={
                                <img
                                    src="/favicon.ico"
                                    alt="sConscious"
                                    width={18}
                                    height={18}
                                    style={{ display: 'block' }}
                                />
                            }
                        />
                        <br/>
                        <br/>
                        {
                            showEmailLogin &&

                            <>
                                <p style={{border: '1px dashed #3c4043', width: '70vw', borderRadius: '5px',  margin: 'auto', opacity: 0.3 }}/>
                                <br/>
                                <form onSubmit={handleManualSignIn} style={{ width: '70vw', display: 'flex', flexDirection: 'column', gap: '15px', margin: '0 auto' }}>
                                    <input
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="username"
                                    className="darkInput"
                                    />
                                    <input
                                    type="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    className="darkInput"
                                    />
                                    {manualLoginError && (
                                    <p style={{ color: 'red', fontSize: '0.9em', textAlign: 'center', margin: '5px 0' }}>
                                        {manualLoginError}
                                    </p>
                                    )}
                                    <ActionButton
                                        label="Login"
                                        type="submit"
                                        onClick={() => {}}
                                        icon={<FaSignInAlt style={{ color: '#ffcc00' }} />}
                                    />
                                </form>
                                <br/>
                                {!showForgotPassword ? (
                                    <span
                                        style={{ fontSize: '0.85em',cursor: 'pointer', textDecoration: 'underline', display: 'block', textAlign: 'center' }}
                                        onClick={() => { setShowForgotPassword(true); setForgotStatus('idle'); setForgotEmail(''); }}
                                    >
                                        Forgot password?
                                    </span>
                                ) : forgotStatus === 'sent' ? (
                                    <p style={{ textAlign: 'center',fontSize: '0.9em' }}>
                                        If this email is registered, a reset link has been sent.
                                    </p>
                                ) : (
                                    <form onSubmit={handleForgotPassword} style={{ width: '70vw', display: 'flex', flexDirection: 'column', gap: '10px', margin: '0 auto' }}>
                                        <input
                                            type="email"
                                            placeholder="Enter your email"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            className="darkInput"
                                        />
                                        <ActionButton
                                            label={forgotStatus === 'sending' ? 'Sending...' : 'Send reset email'}
                                            type="submit"
                                            onClick={() => {}}
                                            icon={<MdMailOutline style={{ color: '#ffcc00' }} />}
                                        />
                                    </form>
                                )}
                                <br/>
                                <p>Or create account</p>
                                <br/>
                                <form onSubmit={createEmailLogin} style={{ width: '70vw', display: 'flex', flexDirection: 'column', gap: '15px', margin: '0 auto' }}>
                                    <input
                                    type="text"
                                    placeholder="Name"
                                    value={newDisplayName}
                                    onChange={(e) => setNewDisplayName(e.target.value)}
                                    autoComplete="name"
                                    className="darkInput"
                                    />
                                    <input
                                    type="email"
                                    placeholder="Email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    autoComplete="new-email"
                                    className="darkInput"
                                    />
                                    <input
                                    type="password"
                                    placeholder="Password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    autoComplete="new-password"
                                    className="darkInput"
                                    />
                                    <ActionButton
                                        label="Create"
                                        type="submit"
                                        onClick={() => {}}
                                        icon={<MdOutlineFollowTheSigns style={{ color: '#ffcc00' }} />}
                                    />
                                </form>
                            </>

                        }

                    </>
            }
            </div>
            )}
        </div>
    );
}